import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface WorkspaceIconContextType {
  iconUpdateTrigger: number;
  immediateIconUrl: string | null;
  triggerIconUpdate: (immediateUrl?: string) => void;
}

const WorkspaceIconContext = createContext<WorkspaceIconContextType>({
  iconUpdateTrigger: 0,
  immediateIconUrl: null,
  triggerIconUpdate: () => {},
});

export const WorkspaceIconProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [iconUpdateTrigger, setIconUpdateTrigger] = useState(0);
  const [immediateIconUrl, setImmediateIconUrl] = useState<string | null>(null);

  const triggerIconUpdate = useCallback((immediateUrl?: string) => {
    if (immediateUrl) {
      setImmediateIconUrl(immediateUrl);
    } else {
      setImmediateIconUrl(null);
    }
    setIconUpdateTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    const handleWorkspaceIconUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ immediateIconUrl?: string }>;
      triggerIconUpdate(customEvent?.detail?.immediateIconUrl);
    };
    window.addEventListener('workspace-icon-updated', handleWorkspaceIconUpdate);
    return () => window.removeEventListener('workspace-icon-updated', handleWorkspaceIconUpdate);
  }, [triggerIconUpdate]);

  return (
    <WorkspaceIconContext.Provider value={{ iconUpdateTrigger, immediateIconUrl, triggerIconUpdate }}>
      {children}
    </WorkspaceIconContext.Provider>
  );
};

export const useWorkspaceIcon = () => {
  const context = useContext(WorkspaceIconContext);
  if (!context) throw new Error('useWorkspaceIcon must be used within WorkspaceIconProvider');
  return context;
};

export const triggerWorkspaceIconUpdate = (immediateIconUrl?: string) => {
  window.dispatchEvent(new CustomEvent('workspace-icon-updated', { detail: { immediateIconUrl } }));
};
